import type { AdaptiveBriefTask, TodayResponse } from '@/api/dashboard';

/**
 * Helpers + types pour la liste "BriefTask du jour" (MVP2.5 T6).
 *
 * Séparé du composant `BriefTask.tsx` pour respecter la règle ESLint
 * `react-refresh/only-export-components` (un fichier composant ne doit
 * pas exporter d'helpers non-composants, sinon HMR perd la trace).
 */

export type BriefTaskAccent = 'violet' | 'cyan' | 'amber' | 'coral' | 'orange' | 'paper';

export interface BriefTaskDescriptor {
  key: string;
  done: boolean;
  label: string;
  /** "3 / 10" — compteur done_today / target_today (MVP2.5 T6). */
  counter: string;
  accent: BriefTaskAccent;
  xp: number;
  onClick: () => void;
}

/** Spec MVP2.5 T6 — mapping label/accent/xp/route par module. */
const TASK_SPECS = [
  {
    key: 'revision',
    label: 'Cartes à réviser',
    accent: 'paper' as const,
    xp: 30,
    path: '/revision-cards',
  },
  {
    key: 'opening',
    label: "Cartes d'ouverture du jour",
    accent: 'violet' as const,
    xp: 30,
    path: '/opening-lab',
  },
  {
    key: 'tactics',
    label: 'Tactiques du jour',
    accent: 'cyan' as const,
    xp: 25,
    path: '/tactics-dojo',
  },
  {
    key: 'endgame',
    label: 'Finales du jour',
    accent: 'amber' as const,
    xp: 25,
    path: '/endgame-forge',
  },
  {
    key: 'autopsy',
    label: "Mini-autopsie d'une partie",
    accent: 'coral' as const,
    xp: 20,
    path: '/game-autopsy',
  },
  {
    key: 'practice',
    label: "Joue ta partie classique",
    accent: 'orange' as const,
    xp: 25,
    path: '/practice-arena/setup',
  },
] as const;

/**
 * Construit la liste des BriefTask "du jour" à partir de la réponse backend
 * `/training/today` enrichie en MVP2.5 T6.
 *
 * Règles :
 *  - Task masquée si `target_today === 0` (rien à faire pour ce module).
 *  - `done = done_today >= target_today` (au lieu de l'ancien `due === 0`).
 *  - Compteur affiché `done_today / target_today`.
 *  - Si la réponse backend ne contient pas encore les nouveaux champs (rollout
 *    en cours), on retombe sur les valeurs 0 → liste vide → message « rien à
 *    faire aujourd'hui ». YAGNI : pas de fallback vers l'ancien comportement
 *    (la spec dit que les tasks doivent toujours refléter "du jour", pas un
 *    cumul 30j cassé).
 *
 * Le callback `onTaskClick(taskKey, path)` reçoit `taskKey` (en plus du path)
 * pour permettre l'award XP `mission_brief` par task (T5 MVP2.5). Les appelants
 * qui n'ont besoin que de naviguer ignorent `taskKey`.
 */
export function buildTasksOfTheDay(
  today: TodayResponse | undefined,
  onTaskClick: (taskKey: string, path: string) => void,
): BriefTaskDescriptor[] {
  const targets: Record<string, number> = {
    opening: today?.opening_target_today ?? 0,
    tactics: today?.tactics_target_today ?? 0,
    endgame: today?.endgames_target_today ?? 0,
    autopsy: today?.autopsy_target_today ?? 0,
    practice: today?.practice_target_today ?? 0,
  };
  const dones: Record<string, number> = {
    opening: today?.opening_done_today ?? 0,
    tactics: today?.tactics_done_today ?? 0,
    endgame: today?.endgame_done_today ?? 0,
    autopsy: today?.autopsy_done_today ?? 0,
    practice: today?.practice_done_today ?? 0,
  };

  // Cas spécial Revision Cards (Release 0.8.0) :
  // pas de `revision_target_today` côté backend — la queue SRS est régie par
  // `cards_due_count`. La task apparaît SI > 0 (jamais "done" tant qu'il en
  // reste : quand le compteur atteint 0, la task disparaît automatiquement).
  // Compteur affiché : "N à faire" (forme `0 / N` rendue par BriefTask).
  const cardsDue = today?.cards_due_count ?? 0;

  const tasks: BriefTaskDescriptor[] = [];
  for (const spec of TASK_SPECS) {
    if (spec.key === 'revision') {
      if (cardsDue <= 0) continue;
      tasks.push({
        key: spec.key,
        done: false,
        label: spec.label,
        counter: `${cardsDue} / ${cardsDue}`,
        accent: spec.accent,
        xp: spec.xp,
        onClick: () => onTaskClick(spec.key, spec.path),
      });
      continue;
    }
    const target = targets[spec.key] ?? 0;
    if (target <= 0) {
      // Pas de target aujourd'hui → task invisible (spec T6).
      continue;
    }
    const done = dones[spec.key] ?? 0;
    tasks.push({
      key: spec.key,
      done: done >= target,
      label: spec.label,
      counter: `${done} / ${target}`,
      accent: spec.accent,
      xp: spec.xp,
      onClick: () => onTaskClick(spec.key, spec.path),
    });
  }
  return tasks;
}

// ─── MVP2.6 Gap 3 (P2.6) — Formatters KPIs Mission Control ──────────────

/**
 * Formate la valeur "Série de jours" pour la tile Mission Control.
 * Wording strict DESIGN.md §6 "froidement motivant" :
 *   - 0 → "—" (jamais "0")
 *   - 1 → "1 j."
 *   - n → "n j."
 * Pas de "🔥", pas de "X jours d'affilée !".
 */
export function formatStreakDays(streak: number | undefined | null): string {
  if (streak == null || streak <= 0) {
    return '—';
  }
  return `${Math.round(streak)} j.`;
}

/**
 * Formate la valeur "Recall ouverture" en pourcentage.
 *   - null/undefined → "—" (insuffisant data)
 *   - n → "n%"
 */
export function formatRecallOpeningPct(pct: number | undefined | null): string {
  if (pct == null) {
    return '—';
  }
  return `${Math.round(pct)}%`;
}

/**
 * Formate la valeur "Blunder rate 7 j." en pourcentage.
 *   - null/undefined → "—"
 *   - 0 → "0%"
 *   - n → "n%"
 */
export function formatBlunderRate(rate: number | undefined | null): string {
  if (rate == null) {
    return '—';
  }
  return `${Math.round(rate)}%`;
}

// ─── MVP2.6 Gap 4 (P2.1) — Adaptive Brief ────────────────────────────────

/**
 * Mapping module → accent visuel (cohérent avec `TASK_SPECS` ci-dessus).
 */
const MODULE_ACCENT: Record<string, BriefTaskAccent> = {
  opening: 'violet',
  tactic: 'cyan',
  endgame: 'amber',
  autopsy: 'coral',
};

/**
 * XP par module pour les tasks adaptatives (barème docs/XP_BAREME.md).
 */
const MODULE_XP: Record<string, number> = {
  opening: 30,
  tactic: 25,
  endgame: 25,
  autopsy: 20,
};

/**
 * Mapping module → route frontend par défaut.
 */
const MODULE_PATH: Record<string, string> = {
  opening: '/opening-lab',
  tactic: '/tactics-dojo',
  endgame: '/endgame-forge',
  autopsy: '/game-autopsy',
};

/**
 * Construit la liste des BriefTask à partir de la réponse `adaptive_brief`
 * du backend (MVP2.6 Gap 4 P2.1). Si la task a un `mode` (cas
 * `tactic_drill_time_pressed`), on l'injecte en query-string pour forcer
 * le composant TacticsDrillPage à démarrer dans ce mode.
 *
 * Les `done`/`counter` sont volontairement absents : l'adaptive brief
 * cible le contexte courant, pas un compteur cumulé. Le composant
 * `BriefTask` doit tolérer `counter=""` (affiche juste le label).
 */
export function buildTasksFromAdaptiveBrief(
  adaptive: AdaptiveBriefTask[] | undefined,
  onTaskClick: (taskKey: string, path: string) => void,
): BriefTaskDescriptor[] {
  if (!adaptive || adaptive.length === 0) {
    return [];
  }
  return adaptive.map<BriefTaskDescriptor>((task) => {
    const accent = MODULE_ACCENT[task.module] ?? 'cyan';
    const xp = MODULE_XP[task.module] ?? 25;
    const basePath = MODULE_PATH[task.module] ?? '/mission-control';
    const path = task.mode !== undefined ? `${basePath}?mode=${task.mode}` : basePath;
    return {
      key: task.key,
      done: false,
      label: task.label,
      counter: '',
      accent,
      xp,
      onClick: () => onTaskClick(task.key, path),
    };
  });
}
